import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../app/hooks";
import { unsubscribeFromAnnouncements } from "../features/portal/slice-extensions/portal-slice-extension";
import { UnsubscribeKind } from "../features/portal/enums/UnsubscribeKind";
import { ROUTES } from "../routes";

/**
 * Landing page for the unsubscribe link in an announcement or session reminder email. The signed token
 * in the URL is the authorization — no sign-in, because someone who cannot log in
 * must still be able to make the mail stop. It unsubscribes on arrival: a member who
 * clicked "unsubscribe" has already made the decision.
 */
const UnsubscribePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [kind, setKind] = useState<UnsubscribeKind>(UnsubscribeKind.Announcements);
  // "invalid" = the server rejected the token (400); "error" = the request
  // itself failed (network, server down) — the link may be perfectly fine.
  const [failed, setFailed] = useState<"invalid" | "error" | null>(null);

  useEffect(() => {
    if (!token) {
      setFailed("invalid");
      return;
    }

    let active = true;
    dispatch(unsubscribeFromAnnouncements(token))
      .unwrap()
      .then((result) => {
        if (!active) return;
        setKind(result.kind ?? UnsubscribeKind.Announcements);
        setOrganizationName(result.organizationName);
      })
      .catch((error: { statusCode?: number }) => {
        if (active) setFailed(error?.statusCode === 400 ? "invalid" : "error");
      });

    return () => {
      active = false;
    };
  }, [token, dispatch]);

  return (
    <section className="mx-auto w-full max-w-md px-4 py-12 text-center">
      <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-95 dark:bg-primary-20">
        <MailX size={26} className="text-primary-40 dark:text-primary-90" />
      </div>

      {failed ? (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {failed === "invalid"
              ? t("unsubscribe.invalidTitle", "This link is not valid")
              : t("unsubscribe.errorTitle", "Something went wrong")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {failed === "invalid"
              ? t(
                  "unsubscribe.invalidBody",
                  "You can change what you receive at any time in the member portal.",
                )
              : t(
                  "unsubscribe.errorBody",
                  "We could not process the link right now. Please try it again in a moment.",
                )}
          </p>
        </>
      ) : organizationName === null ? (
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {t("unsubscribe.working", "One moment…")}
        </p>
      ) : (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("unsubscribe.doneTitle", "You are unsubscribed")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {kind === UnsubscribeKind.SessionReminders
              ? organizationName
                ? t("unsubscribe.remindersDoneBodyNamed", {
                    defaultValue:
                      "You will no longer receive reminders before your booked dates from {{organization}}. Other emails about your own lessons and payments still arrive.",
                    organization: organizationName,
                  })
                : t(
                    "unsubscribe.remindersDoneBody",
                    "You will no longer receive reminders before your booked dates. Other emails about your own lessons and payments still arrive.",
                  )
              : organizationName
              ? t("unsubscribe.doneBodyNamed", {
                  defaultValue:
                    "You will no longer receive announcements from {{organization}}. Emails about your own lessons and payments still arrive.",
                  organization: organizationName,
                })
              : t(
                  "unsubscribe.doneBody",
                  "You will no longer receive announcements. Emails about your own lessons and payments still arrive.",
                )}
          </p>
        </>
      )}

      <Link to={ROUTES.PORTAL_LOGIN} className="btn btn-secondary mt-6 inline-flex">
        {t("unsubscribe.openPortal", "Open the member portal")}
      </Link>
    </section>
  );
};

export default UnsubscribePage;
